import { useCallback, useMemo, useState, useEffect } from 'react';
import { AgGridReact } from 'ag-grid-react';
import {
  ColDef,
  GridReadyEvent,
  GridApi,
  ValueFormatterParams,
  ICellRendererParams,
  IDatasource,
} from 'ag-grid-community';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import * as XLSX from 'xlsx';
import CommonButton from '../../../components/common-components/button/CommonButton';
import CommonDropdown, {
  Option,
} from '../../../components/common-components/dropdown/CommonDropdown';
import styles from './ExpenseManagement.module.css';
import { localeText } from '../../../constants/AgGridLocale';
import { Pencil, Trash2, Plus, RotateCw, Info, Currency } from 'lucide-react';
import { useStore } from '../../../hooks/useStore';
import { observer } from 'mobx-react-lite';
import { IExpenseResult } from '../../../models/expense/ExpenseModel';
import { IUserManagementResult } from '../../../models/user-management/UserManagementModel';
import DeleteConfirmationDialog from './parts/DeleteConfirmationDialog';
import EditExpensePopup from './parts/EditExpensePopup';
import NewExpensePopup from './parts/NewExpensePopup';
import ExpenseDetailPopup from './parts/ExpenseDetailPopup';
import { userManagementService } from '../../../services/modules/userManagement/userManagement.service';

const StatusCellRenderer = (props: ICellRendererParams<IExpenseResult>) => {
  const getStatusClass = (status: string) => {
    switch (status) {
      case 'Pending':
        return styles.statusPending;
      case 'Approved':
        return styles.statusApproved;
      case 'Rejected':
        return styles.statusRejected;
      default:
        return styles.statusPending;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'Pending':
        return 'Beklemede';
      case 'Approved':
        return 'Onaylandı';
      case 'Rejected':
        return 'Reddedildi';
      default:
        return 'Beklemede';
    }
  };

  return (
    <span className={`${styles.statusBadge} ${getStatusClass(props.value)}`}>
      {getStatusText(props.value)}
    </span>
  );
};

const AmountCellRenderer = (props: ICellRendererParams<IExpenseResult>) => {
  const formatAmount = (amount: number): string => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY',
    }).format(amount);
  };

  return (
    <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
      <Currency size={16} style={{ color: '#666' }} />
      {formatAmount(props.value)}
    </span>
  );
};

const ActionCellRenderer = (props: ICellRendererParams<IExpenseResult>) => {
  const { popupStore, expenseStore } = useStore();

  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (props.data) {
      expenseStore.setSelectedExpense(props.data);
      popupStore.setIsEditExpenseOpen(true);
    }
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (props.data?.id) {
      expenseStore.setSelectedExpenseToDelete(props.data.id);
      popupStore.setIsDeleteExpenseConfirmationOpen(true);
    }
  };

  const handleDetail = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (props.data) {
      expenseStore.setSelectedExpense(props.data);
      popupStore.setIsExpenseDetailOpen(true);
    }
  };

  return (
    <div className={styles.actionCell}>
      <button onClick={handleEdit} className={styles.editButton} title="Düzenle">
        <Pencil size={20} />
      </button>
      <button onClick={handleDelete} className={styles.deleteButton} title="Sil">
        <Trash2 size={20} />
      </button>
      <button onClick={handleDetail} className={styles.infoButton} title="Detay">
        <Info size={20} />
      </button>
    </div>
  );
};

const ExpenseManagement = () => {
  const [gridApi, setGridApi] = useState<GridApi<IExpenseResult> | null>(null);
  const [viewportWidth, setViewportWidth] = useState<number>(
    typeof window !== 'undefined' ? window.innerWidth : 1200
  );
  const [users, setUsers] = useState<IUserManagementResult[]>([]);
  const { popupStore, expenseStore, authStore } = useStore();

  useEffect(() => {
    const onResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Admin ise kullanıcı listesini yükle
  useEffect(() => {
    const loadUsers = async () => {
      if (authStore.isAdmin) {
        try {
          const usersList = await userManagementService.getAllUsers();
          const actualUsers = Array.isArray(usersList)
            ? usersList
            : (usersList as any)?.value || [];
          setUsers(actualUsers);
        } catch (error) {
          console.error('Error loading users:', error);
        }
      }
    };

    loadUsers();
  }, [authStore.isAdmin]);

  const dataSource = useMemo((): IDatasource => {
    return {
      getRows: async params => {
        try {
          const result = await expenseStore.listExpenses({
            startRow: params.startRow,
            endRow: params.endRow,
            filterModel: params.filterModel,
            sortModel: params.sortModel,
          });
          if (result) {
            params.successCallback(result.rows, result.lastRow);
          } else {
            params.failCallback();
          }
        } catch (error) {
          console.error('Data loading error:', error);
          params.failCallback();
        }
      },
    };
  }, [expenseStore]);

  const onGridReady = useCallback(
    (params: GridReadyEvent<IExpenseResult>) => {
      setGridApi(params.api);
      expenseStore.setGridApi(params.api);
    },
    [expenseStore]
  );

  const exportToExcel = useCallback(async () => {
    if (!gridApi) return;

    gridApi.showLoadingOverlay();

    try {
      const result = await expenseStore.listExpenses({
        startRow: 0,
        endRow: 1000000,
        filterModel: gridApi.getFilterModel(),
        sortModel: null,
      });

      if (!result?.rows.length) {
        gridApi.hideOverlay();
        return;
      }

      const exportData = result.rows.map(item => ({
        ID: item.id,
        Kullanıcı: item.userName,
        Miktar: item.amount,
        'Harcama Tarihi': new Date(item.expenseDate).toLocaleDateString('tr-TR'),
        Açıklama: item.description,
        Durum:
          item.status === 'Pending'
            ? 'Beklemede'
            : item.status === 'Approved'
            ? 'Onaylandı'
            : 'Reddedildi',
        'Oluşturulma Tarihi': new Date(item.createdDate).toLocaleDateString('tr-TR'),
      }));

      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Harcama Kayitlari');
      XLSX.writeFile(wb, `Harcama_Kayitlari_${new Date().toISOString().split('T')[0]}.xlsx`);
    } catch (error) {
      console.error('Excel export error:', error);
    } finally {
      gridApi.hideOverlay();
    }
  }, [gridApi, expenseStore]);

  const columnDefs = useMemo<ColDef<IExpenseResult>[]>(
    () => [
      {
        headerName: 'İşlemler',
        width: 160,
        pinned: 'left',
        sortable: false,
        filter: false,
        cellRenderer: ActionCellRenderer,
        cellStyle: () => ({
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '0 8px',
          backgroundColor: 'transparent',
        }),
      },
      {
        field: 'id',
        headerName: 'ID',
        width: 80,
        filter: 'agTextColumnFilter',
        filterParams: {
          filterOptions: ['contains'],
          defaultOption: 'contains',
          buttons: ['apply', 'reset'],
          closeOnApply: true,
          suppressAndOrCondition: true,
          maxNumConditions: 1,
        },
      },
      {
        field: 'userName',
        headerName: 'Kullanıcı',
        minWidth: 150,
        flex: 1,
        filter: 'agTextColumnFilter',
        filterParams: {
          filterOptions: ['contains'],
          defaultOption: 'contains',
          buttons: ['apply', 'reset'],
          closeOnApply: true,
          suppressAndOrCondition: true,
          maxNumConditions: 1,
        },
        hide: !authStore.hasAnyRole(['Admin']),
      },
      {
        field: 'amount',
        headerName: 'Miktar',
        width: 150,
        filter: 'agTextColumnFilter',
        filterParams: {
          filterOptions: ['contains'],
          defaultOption: 'contains',
          buttons: ['apply', 'reset'],
          closeOnApply: true,
          suppressAndOrCondition: true,
          maxNumConditions: 1,
        },
        cellRenderer: AmountCellRenderer,
      },
      {
        field: 'expenseDate',
        headerName: 'Harcama Tarihi',
        minWidth: 150,
        filter: 'agTextColumnFilter',
        filterParams: {
          filterOptions: ['contains'],
          defaultOption: 'contains',
          buttons: ['apply', 'reset'],
          closeOnApply: true,
          suppressAndOrCondition: true,
          maxNumConditions: 1,
        },
        valueFormatter: (params: ValueFormatterParams) => {
          if (!params.value) return '';
          return new Date(params.value).toLocaleDateString('tr-TR');
        },
      },
      {
        field: 'description',
        headerName: 'Açıklama',
        minWidth: 200,
        flex: 2,
        filter: 'agTextColumnFilter',
        filterParams: {
          filterOptions: ['contains'],
          defaultOption: 'contains',
          buttons: ['apply', 'reset'],
          closeOnApply: true,
          suppressAndOrCondition: true,
          maxNumConditions: 1,
        },
      },
      {
        field: 'status',
        headerName: 'Durum',
        width: 120,
        filter: 'agTextColumnFilter',
        filterParams: {
          filterOptions: ['contains'],
          defaultOption: 'contains',
          buttons: ['apply', 'reset'],
          closeOnApply: true,
          suppressAndOrCondition: true,
          maxNumConditions: 1,
        },
        cellRenderer: StatusCellRenderer,
        hide: !authStore.hasAnyRole(['Admin']),
      },
      {
        field: 'createdDate',
        headerName: 'Oluşturulma Tarihi',
        minWidth: 160,
        filter: 'agTextColumnFilter',
        filterParams: {
          filterOptions: ['contains'],
          defaultOption: 'contains',
          buttons: ['apply', 'reset'],
          closeOnApply: true,
          suppressAndOrCondition: true,
          maxNumConditions: 1,
        },
        valueFormatter: (params: ValueFormatterParams) => {
          if (!params.value) return '';
          return new Date(params.value).toLocaleDateString('tr-TR');
        },
      },
    ],
    [authStore]
  );

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      resizable: true,
      suppressMovable: true,
      filter: 'agTextColumnFilter',
      filterParams: {
        filterOptions: ['contains'],
        defaultOption: 'contains',
        buttons: ['apply', 'reset'],
        closeOnApply: true,
        suppressAndOrCondition: true,
        maxNumConditions: 1,
      },
    }),
    []
  );

  const isMobile = viewportWidth < 768;
  const isTablet = viewportWidth >= 768 && viewportWidth < 1024;
  const gridRowHeight = isMobile ? 40 : isTablet ? 44 : 48;
  const gridHeaderHeight = isMobile ? 40 : isTablet ? 44 : 48;
  const pageSize = isMobile ? 10 : 20;

  const handleNewExpense = () => {
    popupStore.setIsNewExpenseOpen(true);
  };

  const handleRefresh = () => {
    expenseStore.refreshGrid();
  };

  const handleUserChange = (userId: string | null) => {
    expenseStore.setSelectedUserId(userId === 'all' ? null : userId);
    expenseStore.refreshGrid();
  };

  const getRowStyle = (params: any) => {
    if (!params.data) return undefined;

    const status = params.data.status;

    if (status === 'Approved') {
      return { backgroundColor: '#d4edda' };
    }
    if (status === 'Pending') {
      return { backgroundColor: '#fff3cd' };
    }
    if (status === 'Rejected') {
      return { backgroundColor: '#f8d7da' };
    }

    return undefined;
  };

  const userOptions: Option<string>[] = useMemo(() => {
    const baseOptions: Option<string>[] = [{ value: 'all', label: 'Tüm Kullanıcılar' }];

    if (Array.isArray(users) && users.length > 0) {
      const mappedUserOptions: Option<string>[] = users.map(user => ({
        value: user.id,
        label: `${user.userName}${
          user.firstName && user.lastName ? ` (${user.firstName} ${user.lastName})` : ''
        }`,
      }));
      return [...baseOptions, ...mappedUserOptions];
    }

    return baseOptions;
  }, [users]);

  return (
    <>
      <div className={styles.container}>
        <div className={styles.header}>
          <h2 className={styles.headerTitle}>Harcama Yönetimi</h2>
          {authStore.isAdmin && (
            <div className={styles.userSelector}>
              <CommonDropdown
                options={userOptions}
                value={expenseStore.selectedUserId ? expenseStore.selectedUserId : 'all'}
                onChange={handleUserChange}
                label="Kullanıcı Seçin"
                className={styles.dropdown}
              />
            </div>
          )}
          <div className={styles.buttonContainer}>
            <CommonButton type="primary" onClick={handleNewExpense} leftIcon={<Plus size={18} />}>
              Yeni Harcama Kaydı
            </CommonButton>
            <CommonButton type="primary" onClick={exportToExcel}>
              Excel'e Aktar
            </CommonButton>
            <CommonButton
              type="secondary"
              onClick={handleRefresh}
              leftIcon={<RotateCw size={18} />}
            >
              Yenile
            </CommonButton>
          </div>
        </div>

        <div className={styles.gridContainer}>
          <div className="ag-theme-alpine" style={{ height: '100%', width: '100%' }}>
            <AgGridReact
              rowModelType={'infinite'}
              cacheBlockSize={20}
              maxBlocksInCache={1}
              datasource={dataSource}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              onGridReady={onGridReady}
              getRowStyle={getRowStyle}
              pagination={true}
              paginationPageSize={pageSize}
              rowSelection="multiple"
              rowHeight={gridRowHeight}
              headerHeight={gridHeaderHeight}
              animateRows={true}
              suppressColumnVirtualisation={true}
              suppressRowVirtualisation={true}
              localeText={localeText}
            />
          </div>
        </div>
      </div>

      {popupStore.isNewExpenseOpen && <NewExpensePopup />}
      {popupStore.isEditExpenseOpen && <EditExpensePopup />}
      {popupStore.isExpenseDetailOpen && <ExpenseDetailPopup />}
      <DeleteConfirmationDialog
        isOpen={popupStore.isDeleteExpenseConfirmationOpen}
        onClose={() => popupStore.setIsDeleteExpenseConfirmationOpen(false)}
        onConfirm={async () => {
          if (expenseStore.selectedExpenseToDelete) {
            await expenseStore.deleteExpense(expenseStore.selectedExpenseToDelete);
            expenseStore.setSelectedExpenseToDelete(null);
          }
          popupStore.setIsDeleteExpenseConfirmationOpen(false);
        }}
        title="Harcama Kaydı Silme Onayı"
        message="Bu harcama kaydını silmek istediğinizden emin misiniz?"
      />
    </>
  );
};

export default observer(ExpenseManagement);
